import API from '~/services/api'
import { swymFetch } from '~/services/fetch-swym'

import { getContentReaderData } from '~/features-swym/widgets/content-reader/utils/content-reader-dnd-helper'
import { createExaleadQueryParameters } from '~/features-swym/widgets/content-reader/utils/content-reader-query'

import { fetchSwymContent } from '~/features-shared/content/api/contents'
import type { IExaleadParams } from '~/features-shared/content/api/interfaces/params'
import { ORDERBY, ORDERFIELD } from '~/features-shared/content/api/interfaces/params'
import { contentsParser } from '~/features-shared/content/api/parser-exalead'
import { type IContent, PublicationState } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'

import type { ExaleadService } from '~/interfaces/crud-service'
import { type ExaleadResponse, type SwymError, SwymErrorCode } from '~/interfaces/services-dto'

const parser = contentsParser
const api = API.ContentReader.Exalead.Content

function getPage(index: number, data: { idArray: string[], typeArray: string[] }) {
  let idArr: string[] = []
  let typeArr: string[] = []
  const end = (index + 15) >= data.idArray?.length
    ? data.idArray.length
    : (index + 15)
  if (index < data.idArray?.length) {
    idArr = [...data.idArray.slice(index, end)]
    typeArr = [...data.typeArray.slice(index, end)]
  }
  return {
    idArr,
    typeArr,
  }
}

export const ContentReaderService: ExaleadService<IContent> = {
  count() {
    throw new Error('Function not implemented.')
  },
  async getAll(params) {
    const legacyFormat = false
    const nresults = 15

    // Get ID array from CRA and create
    const crData = await getContentReaderData()
    const dataList = getPage(params?.start as number, { idArray: crData.contentIdArray || [], typeArray: crData.contentTypeArray || [] })
    const refineUsingQuery = createExaleadQueryParameters(dataList?.typeArr, dataList?.idArr)

    const response = await swymFetch
      .post(api, {
        json: {
          params: {
            legacyFormat,
            query: '#all',
            // disable tags fetch when no refine - Query should have better PCS
            with_synthesis: !!params?.refine?.length,
            ...params,
            refineUsingQuery,
            start: 0,
            nresults,
            order_by: undefined,
            order_field: undefined,
            defaultParams: undefined,
          } as IExaleadParams,
        },
      })
      .json<ExaleadResponse>()

    const contentMap = new Map<string, IContent>()
    const data = parser(response)

    // Do not find missing if queried
    if ((params?.query && params.query !== '#all') || (!!params?.refine?.length)) {
      return {
        total: data.length,
        groups: response.groups,
        data,
      }
    }
    data.forEach((item) => {
      contentMap.set(item.id, { ...item })
    })
    const result = await Promise.all(dataList.idArr.map(async (id, index) => {
      const content = contentMap.get(id)

      if (!content) {
        try {
          const type = dataList.typeArr[index] === 'social-event' ? IContentModel.Conference : dataList.typeArr[index]
          return await fetchSwymContent(
            id,
            false,
            type,
          )
        }
        catch (error) {
          const err = error as SwymError | undefined
          // Sign TOU to see content
          const tou_to_sign = err?.errorcode === SwymErrorCode.ErrorCommunityTOUNotSigned
          // Error message is of format, extract subject uri: "Missing user's TOU signature for community #swym:27dsw20116:DEVOPSSKT7XB4Z315243:community:iNcvUGitTcOHvbJLZo0YEA""
          const community_subject_uri = tou_to_sign
            ? err?.errormsg?.split('#')
              .pop()
            : ''
          return {
            id,
            subject_uri: id,
            community_subject_uri,
            title: ' ',
            model: dataList.typeArr[index],
            publication_state: PublicationState.Deleted,
            tou_to_sign,
          }
        }
      }

      return content
    }))

    return {
      total: result.length,
      groups: response.groups,
      data: result as IContent[],
    }
  },

  async getRawData(params = {}) {
    const response = await swymFetch
      .post(api, {
        json: {
          params: {
            legacyFormat: false,
            query: '#all',
            with_synthesis: true, // mandatory to fetch tags
            order_by: ORDERBY.DESC,
            order_field: ORDERFIELD.UPDATE,
            ...params,
            start: 0, // force to fetch nothing but tags
            nresults: 0, // force to fetch nothing but tags
          } as IExaleadParams,
        },
      })
      .json<ExaleadResponse>()

    return {
      groups: response.groups,
      hits: response.hits,
      total: response.nhits,
    }
  },
}
